import { Injectable } from '@angular/core';

/**
 * Responsible for number formatting and rounding.
 */
@Injectable({
  providedIn: 'root'
})
export class NumberFormatterService {

  static getAdjustedPrecision(value: number, format: string, precision: number): number {
    const minPrecision: number = NumberFormatterService.parseMinPrecision(format, precision);
    if (minPrecision === precision) {
      return precision;
    }

    const actualPrecision: number = NumberFormatterService.countDecimals(value, precision);

    if (actualPrecision <= 0) {
      return Math.min(precision, minPrecision);
    }
    if (actualPrecision <= minPrecision) {
      return minPrecision;
    }
    if (actualPrecision < precision) {
      return actualPrecision;
    }
    return precision;
  }

  static parsePrecision(value: number, format: string | undefined): number {
    if (format?.charAt(1) === '?') {
      return NumberFormatterService.getDecimalLength(value);
    }
    const str: string = format.substring(1, 2);
    const parsedString: number = parseInt(str, 10);
    const result = !isNaN(parsedString) ? parsedString : 2;
    return str === '0' ? 0 : result;
  }

  static getPrecisionString(precision: number): string {
    return `.${precision}-${precision}`;
  }

  private static countDecimals(value: number, precision: number): number {
    let result = 0;
    const fixedValue: string = (Number(value)).toFixed(precision);
    const index: number = fixedValue.indexOf('.');

    if (index > 0) {
      // Trim trailing zeros without a regex to avoid any potential super-linear behavior
      let end = fixedValue.length - 1;
      while (end >= 0 && fixedValue.charCodeAt(end) === 48 /* '0' */) {
        end--;
      }
      const dotIndex = fixedValue.lastIndexOf('.', end);
      if (dotIndex !== -1 && end > dotIndex) {
        result = end - dotIndex;
      }
    }
    return result;
  }

  private static parseMinPrecision(format: string, precision: number): number {
    if (format?.charAt(1) === '?') {
      return precision;
    }
    const str: string = format.split('-')[1];
    const parsedString: number = parseInt(str, 10);
    const precisionResult = !isNaN(parsedString) ? parsedString : precision;
    const res: number = str === '0' ? 0 : precisionResult;
    return res > precision ? precision : res;
  }

  private static getDecimalLength(numberToFormat: number): number {
    const value: string = numberToFormat.toString();
    if (value.indexOf('.') >= 0) {
      const len: number = value.split('.')[1].length;
      return len < 2 ? 2 : len;
    } else {
      return 2;
    }
  }
}
